import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { categoryLabel } from "@/lib/constants";
import { CandidateBadges, SuperTalentBadge } from "@/components/CandidateBadges";
import ProfilePhoto from "@/components/ProfilePhoto";
import { ArrowLeft, Star, Trophy, MapPin, Calendar, LayoutDashboard } from "lucide-react";

// Client-facing side nav
const NAV = [
    { to: "/client", label: "Tableau de bord", icon: LayoutDashboard, end: true },
];

export default function CandidatePublicProfile() {
    const { id } = useParams();
    const nav = useNavigate();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/candidates/public/${id}`);
                setP(r.data);
            } catch (err) {
                setError(err?.response?.data?.detail || "Profil introuvable");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading)
        return <DashboardShell title="Profil" nav={NAV}><div className="p-6 text-slate-400 text-sm">Chargement…</div></DashboardShell>;

    if (error)
        return (
            <DashboardShell title="Profil" nav={NAV}>
                <div className="sd-card p-8 text-center">
                    <div className="text-lg font-semibold text-red-600 mb-2">{error}</div>
                    <button onClick={() => nav(-1)} className="btn-ghost-navy text-sm">
                        <ArrowLeft className="inline -mt-1 mr-1" size={16} /> Retour
                    </button>
                </div>
            </DashboardShell>
        );

    const rating = p?.rating ? p.rating.toFixed(1) : null;

    return (
        <DashboardShell title="Profil candidat" nav={NAV}>
            <button
                onClick={() => nav(-1)}
                className="mb-4 text-sm text-slate-500 hover:text-[#0B192C] flex items-center gap-1"
                data-testid="candidate-public-back"
            >
                <ArrowLeft size={16} /> Retour
            </button>

            {/* Hero */}
            <div className="sd-card p-6 md:p-8 mb-6">
                <div className="flex items-start gap-5 flex-wrap">
                    <ProfilePhoto candidateUserId={p.user_id} size={120} />
                    <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h1 className="font-display text-3xl md:text-4xl">
                                {p.first_name} {p.last_name}
                            </h1>
                            {p.is_super_talent && <SuperTalentBadge />}
                        </div>
                        <div className="text-sm text-slate-500 mb-3 flex items-center gap-3 flex-wrap">
                            {p.primary_job && (
                                <span>
                                    <span className="pill pill-gold text-[10px] mr-1">principal</span>
                                    {categoryLabel(p.primary_job)}
                                </span>
                            )}
                            {p.city && (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin size={12} /> {p.city}
                                </span>
                            )}
                            {p.member_since && (
                                <span className="inline-flex items-center gap-1 text-[11px]">
                                    <Calendar size={12} /> Membre depuis {new Date(p.member_since).getFullYear()}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-4 mb-3 flex-wrap">
                            {rating && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5A623]/10 border border-[#F5A623]/30">
                                    <Star size={16} fill="#F5A623" className="text-[#F5A623]" />
                                    <span className="font-bold text-lg text-[#0B192C]">{rating}</span>
                                    <span className="text-xs text-slate-500">/5 · {p.rating_count} évaluation{p.rating_count > 1 ? "s" : ""}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B192C]/5 border border-[#0B192C]/15">
                                <Trophy size={16} className="text-[#0B192C]" />
                                <span className="font-bold text-lg text-[#0B192C]">{p.completed_missions}</span>
                                <span className="text-xs text-slate-500">mission{p.completed_missions > 1 ? "s" : ""} accomplie{p.completed_missions > 1 ? "s" : ""}</span>
                            </div>
                            {p.experience_years > 0 && (
                                <div className="px-3 py-1.5 rounded-full bg-slate-100 text-xs text-slate-600">
                                    {p.experience_years} an{p.experience_years > 1 ? "s" : ""} d'expérience
                                </div>
                            )}
                        </div>
                        {p.badges?.length > 0 && (
                            <div>
                                <CandidateBadges badges={p.badges} size="xs" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bio */}
            {p.bio && (
                <div className="sd-card p-6 mb-6" data-testid="candidate-public-bio">
                    <div className="overline mb-2">Présentation</div>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-line">{p.bio}</p>
                </div>
            )}

            {/* Autres métiers */}
            {p.secondary_jobs?.length > 0 && (
                <div className="sd-card p-6 mb-6">
                    <div className="overline mb-2">Métiers secondaires</div>
                    <div className="flex flex-wrap gap-2">
                        {p.secondary_jobs.map((s) => (
                            <span key={s} className="pill">{categoryLabel(s)}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Job experiences */}
            {p.job_experiences && Object.keys(p.job_experiences).length > 0 && (
                <div className="sd-card p-6 mb-6">
                    <div className="overline mb-3">Expériences par métier</div>
                    <div className="space-y-3">
                        {Object.entries(p.job_experiences).map(([job, xp]) => (
                            <div key={job} className="border-l-2 border-[#F5A623] pl-3">
                                <div className="font-semibold text-sm text-[#0B192C]">{categoryLabel(job)}</div>
                                <div className="text-sm text-slate-600 whitespace-pre-line">{xp}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Languages + certifications */}
            <div className="grid md:grid-cols-2 gap-6">
                {p.languages?.length > 0 && (
                    <div className="sd-card p-6">
                        <div className="overline mb-2">Langues parlées</div>
                        <ul className="text-sm space-y-1">
                            {p.languages.map((l, i) => (
                                <li key={i}>
                                    <b>{l.lang}</b>
                                    {l.level && <span className="text-slate-500"> — {l.level}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {p.certifications?.length > 0 && (
                    <div className="sd-card p-6">
                        <div className="overline mb-2">Certifications</div>
                        <ul className="text-sm space-y-1 list-disc pl-4">
                            {p.certifications.map((c, i) => (
                                <li key={i}>{c}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
}
